/**
 * CopyrightFilingDashCard.jsx
 *
 * Admin card for the Accounting Panel showing:
 *   • Revenue: $15 service fees collected vs CO fees passed through
 *   • Backlog: paid filings awaiting certificate number entry
 *   • Table: all filings — admin records the CO registration number inline
 */
import React, { useCallback, useEffect, useState } from 'react';
import { copyrightFilingAdminApi } from '@/lib/api';
import { BadgeCheck, RefreshCw, FileText, CheckCircle2, Clock, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';

function fmtUsd(v) {
  return `$${Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}
function fmtDate(iso) {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }); }
  catch { return iso.slice(0, 10); }
}

const TYPE_LABELS = { single: 'Single', standard: 'Standard', paper: 'Paper' };

function StatCard({ label, value, sub, tone }) {
  const ring = tone === 'green'  ? 'border-emerald-500/30 bg-emerald-500/5 text-emerald-400'
             : tone === 'amber'  ? 'border-amber-500/30  bg-amber-500/5  text-amber-400'
             : tone === 'rose'   ? 'border-rose-500/30   bg-rose-500/5   text-rose-400'
             : 'border-neutral-700/50 bg-neutral-800/30 text-neutral-300';
  return (
    <div className={cn('rounded-lg border px-4 py-3 space-y-0.5', ring.split(' ').slice(0,2).join(' '))}>
      <p className="text-[10px] uppercase tracking-widest text-neutral-500">{label}</p>
      <p className={cn('text-[22px] font-bold tabular-nums', ring.split(' ')[2])}>{value}</p>
      {sub && <p className="text-[10.5px] text-neutral-600">{sub}</p>}
    </div>
  );
}

function FilingRow({ filing, onRegistered }) {
  const [regNo, setRegNo]   = useState('');
  const [saving, setSaving] = useState(false);
  const isPaid   = filing.payment_status === 'paid';
  const hasReg   = !!filing.registration_number;

  const record = async () => {
    const trimmed = regNo.trim();
    if (!trimmed) { toast.error('Enter the registration number from the Copyright Office.'); return; }
    setSaving(true);
    try {
      await copyrightFilingAdminApi.recordRegistration(filing.filing_id, { registration_number: trimmed });
      toast.success(`Certificate recorded — email sent to ${filing.author_email || 'author'}`);
      setRegNo('');
      onRegistered(filing.filing_id, trimmed);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to record certificate');
    } finally { setSaving(false); }
  };

  return (
    <tr className="border-b border-neutral-800/40 text-[11.5px] align-top">
      {/* Ref */}
      <td className="py-2.5 pr-3 font-mono text-[10.5px] text-neutral-500 whitespace-nowrap">
        {filing.reference_number || filing.filing_id.slice(0,10)}
      </td>
      {/* Author / Project */}
      <td className="py-2.5 pr-3">
        <p className="font-medium text-neutral-200 truncate max-w-[140px]">{filing.project_title || '—'}</p>
        <p className="text-neutral-600 text-[10px] truncate max-w-[140px]">{filing.author_email || '—'}</p>
      </td>
      {/* Type */}
      <td className="py-2.5 pr-3 text-neutral-400">{TYPE_LABELS[filing.filing_type] || filing.filing_type}</td>
      {/* Amount */}
      <td className="py-2.5 pr-3 text-neutral-300 tabular-nums whitespace-nowrap">
        {fmtUsd((filing.total_cents || 0) / 100)}
      </td>
      {/* Date */}
      <td className="py-2.5 pr-3 text-neutral-500 whitespace-nowrap">{fmtDate(filing.paid_at || filing.created_at)}</td>
      {/* Certificate / Action */}
      <td className="py-2.5">
        {hasReg ? (
          <div className="flex items-center gap-1.5">
            <CheckCircle2 className="h-3.5 w-3.5 text-emerald-400 shrink-0" />
            <span className="font-mono text-[10.5px] text-emerald-300">{filing.registration_number}</span>
          </div>
        ) : isPaid ? (
          <div className="flex items-center gap-1.5" data-testid={`reg-row-${filing.filing_id}`}>
            <Input
              placeholder="e.g. TX 9-999-999"
              value={regNo}
              onChange={(e) => setRegNo(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && record()}
              className="h-7 w-28 text-[11px] bg-neutral-800 border-neutral-700 text-neutral-200
                         placeholder:text-neutral-600 focus:ring-amber-500/50"
              data-testid={`reg-input-${filing.filing_id}`}
            />
            <Button
              size="sm"
              className="h-7 px-2 bg-amber-500 hover:bg-amber-400 text-neutral-900 font-semibold text-[11px]"
              onClick={record}
              disabled={saving || !regNo.trim()}
              data-testid={`reg-btn-${filing.filing_id}`}
            >
              {saving ? <RefreshCw className="h-3 w-3 animate-spin" /> : 'Record'}
            </Button>
          </div>
        ) : (
          <span className="flex items-center gap-1 text-[10.5px] text-neutral-600">
            <Clock className="h-3 w-3" /> Pending payment
          </span>
        )}
      </td>
    </tr>
  );
}

export default function CopyrightFilingDashCard() {
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(false);
  const [showAll, setShowAll] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const d = await copyrightFilingAdminApi.dashboard();
      setData(d);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load filing data');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleRegistered = (filingId, regNo) => {
    setData((prev) => {
      if (!prev) return prev;
      const updated = prev.recent_filings.map((f) =>
        f.filing_id === filingId ? { ...f, registration_number: regNo } : f,
      );
      const newBacklog = (prev.totals.backlog_count || 1) - 1;
      const newRegistered = (prev.totals.registered_count || 0) + 1;
      return {
        ...prev,
        recent_filings: updated,
        totals: { ...prev.totals, backlog_count: newBacklog, registered_count: newRegistered },
      };
    });
  };

  const t = data?.totals || {};
  const filings = data?.recent_filings || [];
  const displayed = showAll ? filings : filings.slice(0, 8);

  return (
    <div
      className="rounded-lg border border-neutral-700/50 bg-neutral-900/40 p-5 space-y-4 mt-6"
      data-testid="copyright-filing-dash-card"
    >
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <BadgeCheck className="h-4 w-4 text-amber-400" />
          <p className="text-[13px] font-semibold text-neutral-200">Copyright Office Filings</p>
          {t.backlog_count > 0 && (
            <span className="flex items-center gap-1 text-[10px] bg-rose-500/15 text-rose-300
                             border border-rose-500/30 px-2 py-0.5 rounded-full font-medium">
              <AlertCircle className="h-2.5 w-2.5" />
              {t.backlog_count} awaiting certificate
            </span>
          )}
        </div>
        <Button
          size="sm"
          variant="ghost"
          className="h-7 px-2 text-neutral-600 hover:text-neutral-300"
          onClick={load}
          disabled={loading}
        >
          <RefreshCw className={cn('h-3.5 w-3.5', loading && 'animate-spin')} />
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-5">
        <StatCard
          label="Service fees collected"
          value={fmtUsd(t.service_fees_usd)}
          sub="$15 × paid filings"
          tone="green"
        />
        <StatCard
          label="CO fees passed through"
          value={fmtUsd(t.co_fees_usd)}
          sub="Copyright Office cost"
          tone="amber"
        />
        <StatCard
          label="Total charged to authors"
          value={fmtUsd(t.total_charged_usd)}
          sub="Service + CO fees"
        />
        <StatCard
          label="Paid filings"
          value={t.paid_count ?? '—'}
          sub={`${t.registered_count ?? 0} certificated`}
          tone="green"
        />
        <StatCard
          label="Backlog"
          value={t.backlog_count ?? '—'}
          sub="Paid, awaiting cert"
          tone={t.backlog_count > 0 ? 'rose' : 'green'}
        />
      </div>

      {/* Per-type breakdown */}
      {data?.by_type && (
        <div className="flex flex-wrap gap-3">
          {Object.values(data.by_type).map((bt) => bt.count > 0 && (
            <div key={bt.name} className="text-[11px] bg-neutral-800/40 rounded px-3 py-1.5 border border-neutral-700/40">
              <span className="text-neutral-400 font-medium">{bt.name}</span>
              <span className="ml-2 text-neutral-300">{bt.count} filing{bt.count !== 1 ? 's' : ''}</span>
              <span className="ml-2 text-emerald-400">{fmtUsd(bt.service_fees)}</span>
            </div>
          ))}
        </div>
      )}

      {/* Filings table */}
      {loading && filings.length === 0 ? (
        <div className="flex items-center justify-center py-8 text-neutral-600 text-[12px]">
          <RefreshCw className="h-4 w-4 animate-spin mr-2" />Loading filings…
        </div>
      ) : filings.length === 0 ? (
        <div className="flex flex-col items-center py-8 gap-2 text-neutral-600">
          <FileText className="h-7 w-7 opacity-30" />
          <p className="text-[12px]">No filings yet.</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[640px]">
            <thead>
              <tr className="text-[10px] uppercase tracking-widest text-neutral-600 border-b border-neutral-800">
                <th className="py-1.5 pr-3 text-left font-medium">Ref #</th>
                <th className="py-1.5 pr-3 text-left font-medium">Work / Author</th>
                <th className="py-1.5 pr-3 text-left font-medium">Type</th>
                <th className="py-1.5 pr-3 text-left font-medium">Amount</th>
                <th className="py-1.5 pr-3 text-left font-medium">Date</th>
                <th className="py-1.5 text-left font-medium">Certificate #</th>
              </tr>
            </thead>
            <tbody>
              {displayed.map((f) => (
                <FilingRow
                  key={f.filing_id}
                  filing={f}
                  onRegistered={handleRegistered}
                />
              ))}
            </tbody>
          </table>
          {filings.length > 8 && (
            <button
              type="button"
              onClick={() => setShowAll((v) => !v)}
              className="mt-2 text-[11px] text-neutral-500 hover:text-neutral-300"
            >
              {showAll ? 'Show fewer' : `Show all ${filings.length} filings`}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
